import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Circle } from 'lucide-react';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, buttonVariants, cn, FormField, PasswordInput } from '@payment-plan/ui';
import { AuthCard, ENLACE_CLASS } from './AuthCard';
import { REGLAS_CONTRASENA, definirContrasenaSchema, type DefinirContrasenaValues } from './contrasena';
import { ApiError, apiClient } from '../../lib/api-client';

export const AVISO_CONTRASENA_DEFINIDA = 'Tu contraseña quedó definida. Ya puedes iniciar sesión.';

function EnlaceInvalido(): React.JSX.Element {
  return (
    <AuthCard title="El enlace no es válido" description="Puede que ya se haya usado o que haya vencido.">
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Por seguridad, cada enlace sirve una sola vez y tiene un tiempo límite. Solicita uno nuevo con tu correo.
        </p>
        {/* Link con el estilo del botón (Button asChild no admite su spinner como hermano). */}
        <Link to="/recuperar" className={cn(buttonVariants(), 'w-full')}>
          Solicitar un enlace nuevo
        </Link>
        <p className="text-center">
          <Link to="/login" className={ENLACE_CLASS}>
            Volver a iniciar sesión
          </Link>
        </p>
      </div>
    </AuthCard>
  );
}

/**
 * fix-60 · alcance extra del dev: pantalla pública del enlace de invitación (asesor, administrador) y
 * de recuperación. Valida en tiempo real las reglas de la contraseña; el back vuelve a validarlas.
 */
export function DefinirContrasenaPage(): React.JSX.Element {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const navigate = useNavigate();
  const [enlaceInvalido, setEnlaceInvalido] = React.useState(false);
  const [serverError, setServerError] = React.useState<string | null>(null);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting, submitCount },
  } = useForm<DefinirContrasenaValues>({
    resolver: zodResolver(definirContrasenaSchema),
    mode: 'onChange',
    defaultValues: { contrasena: '', confirmacion: '' },
  });
  const contrasena = watch('contrasena');

  if (!token || enlaceInvalido) {
    return <EnlaceInvalido />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setServerError(null);
    try {
      await apiClient.setPassword(token, values.contrasena);
      navigate('/login', { replace: true, state: { aviso: AVISO_CONTRASENA_DEFINIDA } });
    } catch (error) {
      if (error instanceof ApiError && error.status === 400 && /enlace/i.test(error.message)) {
        setEnlaceInvalido(true);
        return;
      }
      setServerError(error instanceof Error ? error.message : 'No fue posible definir la contraseña.');
    }
  });

  return (
    <AuthCard title="Define tu contraseña" description="Con ella ingresarás a tu espacio de trabajo.">
      <form className="space-y-5" onSubmit={(event) => void onSubmit(event)} noValidate>
        <FormField
          label="Nueva contraseña"
          htmlFor="contrasena"
          required
          error={submitCount > 0 ? errors.contrasena?.message : undefined}
        >
          <PasswordInput
            id="contrasena"
            autoComplete="new-password"
            autoFocus
            aria-describedby="reglas-contrasena"
            error={submitCount > 0 && Boolean(errors.contrasena)}
            {...register('contrasena')}
          />
        </FormField>

        <ul id="reglas-contrasena" aria-label="Requisitos de la contraseña" aria-live="polite" className="space-y-1.5">
          {REGLAS_CONTRASENA.map((regla) => {
            const cumple = regla.cumple(contrasena);
            return (
              <li
                key={regla.id}
                data-regla={regla.id}
                data-cumple={cumple}
                className={cn('flex items-center gap-2 text-sm', cumple ? 'text-success' : 'text-muted-foreground')}
              >
                {cumple ? <Check className="h-4 w-4" aria-hidden="true" /> : <Circle className="h-3.5 w-3.5" aria-hidden="true" />}
                <span>{regla.texto}</span>
                <span className="sr-only">{cumple ? '(cumple)' : '(pendiente)'}</span>
              </li>
            );
          })}
        </ul>

        <FormField label="Confirmar contraseña" htmlFor="confirmacion" required error={errors.confirmacion?.message}>
          <PasswordInput
            id="confirmacion"
            autoComplete="new-password"
            error={Boolean(errors.confirmacion)}
            {...register('confirmacion')}
          />
        </FormField>

        {serverError && <Alert variant="destructive" role="alert">{serverError}</Alert>}

        <Button className="w-full" type="submit" disabled={isSubmitting} loading={isSubmitting}>
          {isSubmitting ? 'Guardando...' : 'Guardar contraseña'}
        </Button>
        <p className="text-center">
          <Link to="/login" className={ENLACE_CLASS}>
            Volver a iniciar sesión
          </Link>
        </p>
      </form>
    </AuthCard>
  );
}
